import { useMemo, useState } from "react";
import classNames from "classnames";
import {
  Button,
  Card,
  CardContent,
  CardFooter,
  Chip,
  Division,
  Flex,
  Heading,
  Icon,
  Icons,
  Paragraph,
  Skeleton,
  Span,
  Table,
  TableColumnType,
} from "@ir/ui";
import { useAnalyticsKPI, useAnalyticsMobile } from "@/hooks";
import { AnalyticsDeductionDimension, ANALYTICS_CHECK_DATE_RANGE_LABEL, ScoreCardTrend } from "@/store/analytics";
import { getRowsForDimension } from "@/core/utils/filterDeductionRowsByDimension";
import {
  DeductionByTypeTableRow,
  groupDeductionRowsWithOther,
} from "@/store/analytics/groupDeductionRowsWithOther";
import EmptyKpiCard from "./EmptyKpiCard";
import "../../styles/Analytics.page.scss";

const ROW_COLORS = [
  "var(--secondary-400)",
  "var(--primary-500)",
  "var(--warning-500)",
  "var(--success-500)",
  "var(--error-400)",
  "var(--grey-300)",
  "var(--secondary-600)",
  "var(--success-300)",
  "var(--warning-300)",
  "var(--error-300)",
  "var(--grey-300)",
  "var(--secondary-600)",
  "var(--success-300)",
  "var(--warning-300)",
  "var(--error-300)",
];

const HEADING = "Deduction by Type/GL Code";

type TableDimension = "type" | "reason";

const formatAmount = (value: number) =>
  new Intl.NumberFormat("en-US", {
    notation: "compact",
    compactDisplay: "short",
    maximumFractionDigits: 0,
  })
    .format(value)
    .replace(/^/, "$");

const formatShare = (value: number) => `${value?.toFixed(2)}%`;

const getVsPriorColor = (trend: ScoreCardTrend | null) => {
  if (trend === "UP") return "text-error-700";
  if (trend === "DOWN") return "text-success-700";
  return "text-grey-400";
};

const VsPriorCell = ({ vsPrior, trend }: { vsPrior: number | null; trend: ScoreCardTrend | null }) => {
  if (vsPrior === null || trend === null) {
    return <Span className="text-grey-400">—</Span>;
  }

  return (
    <Flex align="center" gap={0.25} className={getVsPriorColor(trend)} wrap="no-wrap">
      {trend === "UP" && <Icon name={Icons.ArrowNarrowUp} size="small" />}
      {trend === "DOWN" && <Icon name={Icons.ArrowNarrowDown} size="small" />}
      {trend === "NEUTRAL" && <Icon name={Icons.ArrowRight} size="small" />}
      <Span>{Math.abs(vsPrior)}%</Span>
    </Flex>
  );
};

type DeductionTableRow = DeductionByTypeTableRow & { fill: string };

function renderLabelCell(_value: unknown, record: DeductionTableRow) {
  return <Flex align="center" gap={1} wrap="no-wrap">
    <Span className="font-medium text-grey-800">{record.label}</Span>
  </Flex>;
}

function renderAmountCell(value: unknown) {
  return <Span className="text-grey-800">{formatAmount(Number(value))}</Span>;
}

function renderCountCell(value: unknown) {
  return <Span className="text-grey-800">{Number(value).toLocaleString()}</Span>;
}

function renderShareCell(value: unknown) {
  return <Span className="text-grey-800">{formatShare(Number(value))}</Span>;
}

function renderVsPriorCell(_value: unknown, record: DeductionTableRow) {
  return <VsPriorCell vsPrior={record.vsPrior} trend={record.trend} />;
}

const getDeductionsByTypeAndReasonColumns = (
  selectedDimension: TableDimension
): TableColumnType<DeductionTableRow>[] => [
    {
      title: selectedDimension === "type" ? "Type" : "GL Code",
      dataIndex: "label",
      key: "label",
      cellRender: renderLabelCell,
    },
    {
      title: "Amount",
      dataIndex: "amount",
      key: "amount",
      align: "right",
      width: 100,
      cellRender: renderAmountCell,
    },
    {
      title: "Count",
      dataIndex: "count",
      key: "count",
      align: "right",
      width: 100,
      cellRender: renderCountCell,
    },
    {
      title: "Share",
      dataIndex: "share",
      key: "share",
      align: "right",
      width: 100,
      cellRender: renderShareCell,
    },
    {
      title: "vs Prior",
      dataIndex: "vsPrior",
      key: "vsPrior",
      align: "left",
      width: 130,
      cellRender: renderVsPriorCell,
    },
  ];

const TYPE_REASON_SKELETON_ROWS = ["row-1", "row-2", "row-3", "row-4", "row-5", "row-6"] as const;

const getRowFillColor = (row: DeductionByTypeTableRow, index: number) =>
  row.isOther ? ROW_COLORS.at(-1)! : ROW_COLORS[index % (ROW_COLORS.length - 1)];

const DeductionsByTypeAndReasonSkeleton = () => (
  <Division style={{ padding: "var(--spacing-3)" }}>
    {TYPE_REASON_SKELETON_ROWS.map((rowKey, index) => (
      <Flex
        key={rowKey}
        align="center"
        justifyContent="space-between"
        style={{ marginBottom: index < TYPE_REASON_SKELETON_ROWS.length - 1 ? 20 : 0 }}
      >
        <Flex align="center" gap={1}>
          <Skeleton width={16} height={16} style={{ borderRadius: 4 }} />
          <Skeleton width={80} height={12} style={{ borderRadius: 4 }} />
        </Flex>
        <Skeleton width={48} height={12} style={{ borderRadius: 4 }} />
        <Skeleton width={40} height={12} style={{ borderRadius: 4 }} />
        <Skeleton width={32} height={12} style={{ borderRadius: 4 }} />
        <Skeleton width={56} height={12} style={{ borderRadius: 4 }} />
      </Flex>
    ))}
  </Division>
);

const DeductionsByTypeAndReasonCard = () => {
  const [selectedDimension, setSelectedDimension] = useState<TableDimension>("type");
  const { deductionsByTypeAndReason } = useAnalyticsKPI();
  const isMobile = useAnalyticsMobile();

  const apiDimension: AnalyticsDeductionDimension = selectedDimension === "type" ? "TYPE" : "REASON";

  const filteredRows = useMemo(
    () => getRowsForDimension(deductionsByTypeAndReason.groups, apiDimension),
    [deductionsByTypeAndReason.groups, apiDimension]
  );

  const tableRows = useMemo(() => {
    const groupedRows = groupDeductionRowsWithOther(filteredRows, apiDimension);

    return groupedRows.map((row, index) => ({
      ...row,
      fill: getRowFillColor(row, index),
    }));
  }, [filteredRows, apiDimension]);

  const columns = useMemo(
    () => getDeductionsByTypeAndReasonColumns(selectedDimension),
    [selectedDimension]
  );

  const columnLabel = selectedDimension === "type" ? "Type" : "GL Code";

  const cardContent = (() => {
    if (deductionsByTypeAndReason.isLoading) {
      return <DeductionsByTypeAndReasonSkeleton />;
    }
    if (tableRows.length === 0) {
      return (
        <Flex justifyContent="center" align="center" style={{ height: 350 }}>
          <EmptyKpiCard
            title="No data available"
            description="No deductions recorded for the selected period. Try adjusting the date range."
            icon={Icons.TableIcon}
          />
        </Flex>
      );
    }
    return (
      <Division className="analytics-table-scroll">
        <Table<DeductionTableRow>
          key={apiDimension}
          columns={columns}
          dataSource={tableRows}
          border="row"
          aria-label={`Deductions by ${columnLabel} table`}
        />
      </Division>
    );
  })();

  return (
    <Card className="bg-white border-1 border-color-grey-50">
      <Flex
        className="analytics-kpi-card__header px-4 py-3 border-bottom-1 border-color-grey-25"
        justifyContent="space-between"
        align="center"
      >
        <Flex direction="column">
          <Heading text={HEADING} level={6} className="m-0 text-grey-500 font-bold" />
          {!isMobile && (
            <Paragraph className="m-0 text-grey-400">Breakdown by deduction {columnLabel.toLowerCase()}</Paragraph>
          )}
        </Flex>
        {!deductionsByTypeAndReason.isLoading && (
          <Button.Group className="p-1 bg-grey-10 border-1 border-color-grey-100 rounded-small">
            <Button
              size="small"
              color="grey"
              variant={selectedDimension === "type" ? "solid" : "clear"}
              onPress={() => setSelectedDimension("type")}
              className={classNames("m-0", {
                "bg-grey-25": selectedDimension === "type",
                "text-grey-600": selectedDimension === "type",
              })}
            >
              Type
            </Button>
            <Button
              size="small"
              color="grey"
              variant={selectedDimension === "reason" ? "solid" : "clear"}
              onPress={() => setSelectedDimension("reason")}
              className={classNames("m-0", {
                "bg-grey-25": selectedDimension === "reason",
                "text-grey-600": selectedDimension === "reason",
              })}
            >
              GL Code
            </Button>
          </Button.Group>
        )}
      </Flex>
      <CardContent style={{ minHeight: !isMobile ? 370 : "auto" }}>{cardContent}</CardContent>
      <CardFooter
        className="analytics-card-footer bg-grey-10 text-grey-500"
        style={{ borderBottomLeftRadius: ".75rem", borderBottomRightRadius: ".75rem" }}
      >
        <Flex gap={0.5} align="center" wrap="wrap">
          Filter applied:{" "}
          <Chip label={ANALYTICS_CHECK_DATE_RANGE_LABEL} variant="filled" color="secondary" borderless />
          <Chip label="Purchaser" variant="filled" color="secondary" borderless />
        </Flex>
      </CardFooter>
    </Card>
  );
};

export default DeductionsByTypeAndReasonCard;
